import styled, { keyframes } from 'styled-components';
import { FormControl } from '@material-ui/core';
import { DSButton, DSLink, DSText } from '@engenharia/ds-react';

import Colors from '../../styles/theme/colors';
import spaces from '../../styles/theme/spaces';
import { devices } from '../../styles/theme/devices';
import { Hidden } from '../../components/utilities/Hidden';
import BackgroundImage from '../../assets/logo-symbol.svg';
import { forMd } from '../../config/_variables';

const Container = styled.div`
  align-items: center;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 100vh;
  padding: 56px 0;

  @media ${devices.laptop} {
    background-attachment: fixed;
    background-image: url(${BackgroundImage});
    background-position: -490px 160px;
    background-repeat: no-repeat;
  }
`;

const Section = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  margin: 0 auto;
  max-width: 1140px;
  text-align: center;

  @media ${devices.laptop} {
    display: grid;
    grid-column-gap: ${spaces.small};
    grid-template-columns: repeat(12, 1fr);
  }
`;

const ContainerBanner = styled.div`
  padding: 0 24px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  ${forMd} {
    padding: 0;
  }
`;

const CallToActionBox = styled(Hidden)`
  grid-column: 2 / span 4;
`;

const Card = styled.div`
  align-items: center;
  background-color: ${Colors.default.white};
  display: flex;
  flex-direction: column;
  flex: 1;
  height: fit-content;
  justify-content: center;
  padding: ${spaces.medium};
  text-align: center;
  max-width: 433px;

  @media ${devices.laptop} {
    border-radius: 4px;
    grid-column: 7 / span 5;
    padding: ${spaces.small};
  }

  @media ${devices.tablet} {
    border: solid 1px ${Colors.default.lightGrey};
    height: auto;
  }
`;

const Image = styled.img`
  height: 48px;
  object-fit: contain;
  width: 100%;

  @media ${devices.laptop} {
    height: 147px;
  }
`;

const SignupBox = styled.div`
  margin-top: calc(${spaces.xs} / 2);
`;

const SignInFreeButton = styled(DSButton)`
  height: 36px;
  margin-bottom: ${spaces.small};
  margin-top: calc(${spaces.xs} / 2);

  @media ${devices.laptop} {
    margin-bottom: 0;
    margin-top: ${spaces.xs};
  }
  
  p {
    letter-spacing: normal;
  }
`;

const LinkButton = styled(DSLink)`
  height: auto;
  min-height: 36px;
  margin: 0;
  letter-spacing: normal;
`;

const UnconfirmedAccountPrefix = styled.span`
  color: #000;
  font-weight: 400;
`;

const UnconfirmedAccountHighlight = styled.span`
  font-weight: 600;
`;

const CheckboxContainer = styled(FormControl)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  margin-top: 8px;

  & > .MuiCheckbox-root {
    padding: 0;
    color: #1a73e8;
  }

  & > p {
    color: #757575;
    letter-spacing: normal;
  }
`;

const ChoiceGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  margin-bottom: 8px;
`;

const MagicLinkChoiceBtn = styled.button`
  width: 100%;
  height: 48px;
  background: #1a73e8;
  border: none;
  border-radius: 12px;
  color: #ffffff;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    background: #0a57bb;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(26, 115, 232, 0.35);
  }
`;

const EmailPasswordChoiceBtn = styled.button`
  width: 100%;
  height: 48px;
  background: #ffffff;
  border: 1.5px solid #e5e7eb;
  border-radius: 12px;
  color: #374151;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 0;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
  }
`;

const EmailPasswordBtnContent = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0 44px;
`;

const EmailPasswordBtnPrefix = styled.span`
  color: #6b7280;
  font-weight: 400;
  white-space: nowrap;
`;

const EmailPasswordBtnHighlight = styled.strong`
  color: #ff6b35;
  font-weight: 700;
  margin-left: 4px;
  white-space: nowrap;
`;

const EmailPasswordBtnIcon = styled.span`
  position: absolute;
  right: 16px;
  display: flex;
  align-items: center;
  color: #6b7280;
  font-size: 20px;
`;

const BackBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-bottom: 12px;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    color: #374151;
  }
`;

const MagicLinkTitle = styled(DSText)`
  display: block;
  font-size: 15px;
  font-weight: 600;
  text-align: left;
  color: #111827;
  margin: 0 0 4px;
  line-height: 1.4;
`;

const successPop = keyframes`
  0%   { transform: scale(0.8); opacity: 0; }
  60%  { transform: scale(1.05); }
  100% { transform: scale(1); opacity: 1; }
`;

const SuccessBox = styled.div`
  animation: ${successPop} 0.45s ease both;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 20px 16px;
  gap: 8px;
  font-family: inherit;
`;

const SuccessIcon = styled.div`
  font-size: 48px;
  line-height: 1;
`;

const SuccessTitle = styled(DSText)`
  font-size: 17px;
  font-weight: 700;
  color: #111827;
  margin: 0;
`;

const SuccessSubtitle = styled(DSText)`
  font-size: 13px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
`;

const ResendRow = styled.div`
  margin-top: 6px;
  font-size: 13px;
  color: #9ca3af;
  font-family: inherit;
`;

const ResendButton = styled(DSButton)`
  color: #1a73e8;

  &:hover {
    color: #1f41b6;
  }
`;

const OtherWaysButton = styled.button`
  background: none;
  border: none;
  padding: 8px 0;
  margin: 0 auto;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #6b7280;
  cursor: pointer;
  display: block;
  text-decoration: none;

  &:hover {
    color: #374151;
    text-decoration: underline;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
`;

const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 12px;
  width: 100%;
  max-width: 420px;
  padding: 24px;
  position: relative;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`;

const ModalCloseButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  line-height: 1;

  &:hover {
    color: #111827;
  }
`;

export {
  BackBtn,
  CallToActionBox,
  Card,
  ChoiceGroup,
  Container,
  EmailPasswordChoiceBtn,
  EmailPasswordBtnContent,
  EmailPasswordBtnPrefix,
  EmailPasswordBtnHighlight,
  EmailPasswordBtnIcon,
  Image,
  LinkButton,
  UnconfirmedAccountPrefix,
  UnconfirmedAccountHighlight,
  MagicLinkChoiceBtn,
  MagicLinkTitle,
  Section,
  SignInFreeButton,
  SignupBox,
  ContainerBanner,
  CheckboxContainer,
  SuccessBox,
  SuccessIcon,
  SuccessTitle,
  SuccessSubtitle,
  ResendRow,
  ResendButton,
  OtherWaysButton,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
};
